import { faCircleCheck, faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateSimple, PaymentMethodTypeEnum } from '@supply-mover-2-0/core';
import {
	formPaymentMethodAnswer,
	parseFormPaymentMethodAnswer,
	SENT_FORM_ROLES,
	type FormDefinitionModel,
	type FormFieldDefinitionModel,
	type FormFieldErrorCode,
	type PublicSentFormModel,
	type PublicSentFormState,
	type SentFormPaymentMethodOptionModel,
} from '@supply-mover-2-0/shared';
import {
	FormField,
	FormRenderer,
	Toast,
	useFormRenderer,
	useTheme,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	PaymentLinkShell,
	PaymentLinkSupportLine,
} from '../components/paymentLinks/PaymentLinkShell';
import {
	PortalAddPaymentMethod,
	type PortalPaymentMethodApiModel,
} from '../components/PortalAddPaymentMethod';
import { useTurnstile } from '../hooks/useTurnstile';
import {
	addSentFormPaymentMethod,
	fetchSentForm,
	getSentFormCaptureContext,
	SentFormRequestError,
	sentFormGatewayConfig,
	submitSentForm,
} from '../services/sentFormService';

const STATE_COPY: Record<Exclude<PublicSentFormState, 'active'>, { title: string; body: string }> = {
	unavailable: {
		title: 'This form is no longer available',
		body: 'It has been completed, withdrawn or replaced by a newer link. If you need a copy, please contact us.',
	},
	expired: {
		title: 'This link has expired',
		body: 'For your security, links only work for a limited time. Please contact us for a new one.',
	},
	invalid: {
		title: 'This form is no longer available',
		body: 'Check that you opened the whole link from your email or text, or contact us.',
	},
};

/**
 * PD-13768 — `/form/:token`: a form a staff member sent to complete and sign, without
 * signing in. Details already on file are filled in; those the customer may change can be
 * corrected (staff review each change), the rest are shown read-only. The payment method
 * question lists the account's saved cards and bank accounts by their last four only, and
 * can add a new one, which is saved to the account at once. Signing records the name,
 * title, time, address and device, and a fingerprint of exactly what was shown.
 */
export function SentFormPage(): JSX.Element {
	const { t } = useTranslation();
	const { token = '' } = useParams<{ token: string }>();
	const [form, setForm] = useState<PublicSentFormModel | null>(null);
	const [state, setState] = useState<PublicSentFormState | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [done, setDone] = useState<string | null>(null);

	const load = useCallback(async (): Promise<void> => {
		try {
			const loaded = await fetchSentForm(token);
			setForm(loaded);
			setState(loaded.state);
		} catch (error) {
			if (error instanceof SentFormRequestError && error.state != null) {
				setState(error.state);
				return;
			}
			setLoadError(error instanceof Error ? error.message : 'This page could not be loaded.');
		}
	}, [token]);

	useEffect(() => {
		void load();
	}, [load]);

	if (done != null) {
		return (
			<PaymentLinkShell secureLabel="Secure form" showSignIn={false}>
				<div className="bg-brand-surface border border-brand-border rounded-xl p-6 sm:p-8 text-center space-y-3" data-testid="sent-form-done">
					<FontAwesomeIcon icon={faCircleCheck} className="text-4xl text-success" />
					<h1 className="text-2xl font-semibold text-default">{t('Signed')}</h1>
					<p className="text-sm text-brand-muted">{t(done)}</p>
					<div className="pt-2 flex justify-center">
						<PaymentLinkSupportLine />
					</div>
				</div>
			</PaymentLinkShell>
		);
	}
	if (loadError != null) {
		return (
			<PaymentLinkShell secureLabel="Secure form" showSignIn={false}>
				<p className="text-sm text-danger text-center py-10">{t(loadError)}</p>
			</PaymentLinkShell>
		);
	}
	if (state == null) {
		return (
			<PaymentLinkShell secureLabel="Secure form" showSignIn={false}>
				<div className="flex items-center justify-center py-16">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-portal-accent" />
				</div>
			</PaymentLinkShell>
		);
	}
	if (state !== 'active' || form == null || form.definition == null) {
		const copy = STATE_COPY[state === 'active' ? 'unavailable' : state];
		return (
			<PaymentLinkShell secureLabel="Secure form" showSignIn={false}>
				<div className="bg-brand-surface border border-brand-border rounded-xl p-6 sm:p-8 text-center space-y-3" data-testid="sent-form-state">
					<FontAwesomeIcon icon={faCircleExclamation} className="text-4xl text-brand-muted" />
					<h1 className="text-2xl font-semibold text-default">{t(copy.title)}</h1>
					<p className="text-sm text-brand-muted">{t(copy.body)}</p>
					<div className="pt-2 flex justify-center">
						<PaymentLinkSupportLine />
					</div>
				</div>
			</PaymentLinkShell>
		);
	}
	return (
		<PaymentLinkShell secureLabel="Secure form" showSignIn={false}>
			<SentFormBody token={token} form={form} onDone={setDone} onGone={setState} />
		</PaymentLinkShell>
	);
}

function SentFormBody({
	token,
	form,
	onDone,
	onGone,
}: {
	token: string;
	form: PublicSentFormModel;
	onDone: (message: string) => void;
	onGone: (state: PublicSentFormState) => void;
}): JSX.Element {
	const { t } = useTranslation();
	const definitionModel = form.definition;
	const definition = useMemo<FormDefinitionModel>(
		() => ({
			fields: (definitionModel?.fields ?? []).map((field) =>
				field.fieldType === 'paymentMethod'
					? { ...field, isRequired: form.paymentMethodRequired }
					: field,
			),
			rules: definitionModel?.rules ?? [],
			layout: definitionModel?.layout ?? null,
		}),
		[definitionModel, form.paymentMethodRequired],
	);
	const readOnlyKeys = useMemo(
		() =>
			new Set(
				(definitionModel?.fields ?? [])
					.filter((field) => field.access === 'locked')
					.map((field) => field.fieldKey),
			),
		[definitionModel],
	);
	const initial = useMemo(() => {
		const values: Record<string, unknown> = { ...form.values };
		// A saved default method starts chosen.
		const paymentField = definitionModel?.fields.find((field) => field.fieldType === 'paymentMethod');
		const defaultMethod = form.paymentMethods.find((method) => method.isDefault);
		if (paymentField != null && values[paymentField.fieldKey] == null && defaultMethod != null) {
			values[paymentField.fieldKey] = formPaymentMethodAnswer(defaultMethod.id);
		}
		return values;
	}, [form, definitionModel]);
	const state = useFormRenderer(definition, initial);
	const [methods, setMethods] = useState<Array<SentFormPaymentMethodOptionModel>>(form.paymentMethods);
	const [submitting, setSubmitting] = useState(false);

	const submit = async (): Promise<void> => {
		const result = state.attempt();
		if (!result.valid) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
			return;
		}
		const signatureField = definition.fields.find((field) => field.fieldType === 'signature');
		const signerField = definition.fields.find((field) => field.fieldOptions?.role === SENT_FORM_ROLES.signerName);
		const titleField = definition.fields.find((field) => field.fieldOptions?.role === SENT_FORM_ROLES.signerTitle);
		setSubmitting(true);
		try {
			const message = await submitSentForm(token, {
				answers: result.answers,
				signerName: signerField != null ? String(result.answers[signerField.fieldKey] ?? '') : '',
				signerTitle: titleField != null ? String(result.answers[titleField.fieldKey] ?? '') : null,
				signatureMode:
					signatureField != null ? (state.signatureModes[signatureField.fieldKey] ?? 'draw') : 'draw',
			});
			onDone(message);
		} catch (error) {
			if (error instanceof SentFormRequestError) {
				if (error.state != null && error.state !== 'active') {
					onGone(error.state);
					return;
				}
				if (error.errors != null) {
					state.setServerErrors({ errors: error.errors as Record<string, FormFieldErrorCode> });
				}
			}
			Toast.error(t(error instanceof Error ? error.message : 'The form could not be sent.'));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="space-y-6" data-testid="sent-form-page">
			<section className="bg-brand-surface border border-brand-border rounded-xl p-5 sm:p-6 space-y-2">
				<h1 className="text-2xl font-semibold text-default">{t(form.formName ?? '')}</h1>
				<p className="text-sm text-brand-muted">{t(form.clientName ?? '')}</p>
				<p className="text-sm text-default">
					{t('Please check the details we have on file, complete the rest, read the terms and sign.')}
				</p>
				{form.expiresAt != null && (
					<p className="text-xs text-brand-muted">
						{`${t('This link works until')} ${t(formatDateSimple(form.expiresAt))}.`}
					</p>
				)}
			</section>
			<section className="bg-brand-surface border border-brand-border rounded-xl p-5 sm:p-6">
				<FormRenderer
					definition={definition}
					state={state}
					idPrefix="sent-form"
					readOnlyKeys={readOnlyKeys}
					lookups={form.lookups}
					disabled={submitting}
					renderCustomField={(field) =>
						field.fieldType === 'paymentMethod' ? (
							<PaymentMethodQuestion
								token={token}
								form={form}
								field={field}
								methods={methods}
								value={state.values[field.fieldKey]}
								error={state.visible.fields[field.fieldKey] ?? null}
								onChange={(value) => {
									state.setValue(field.fieldKey, value);
									state.touch(field.fieldKey);
								}}
								onAdded={(method) => {
									setMethods((previous) => [...previous.filter((candidate) => candidate.id !== method.id), method]);
									state.setValue(field.fieldKey, formPaymentMethodAnswer(method.id));
								}}
							/>
						) : null
					}
				/>
				<div className="pt-6 flex flex-col items-center gap-3">
					<button
						type="button"
						disabled={submitting || state.pendingUploads > 0}
						onClick={() => {
							void submit();
						}}
						className="w-full sm:w-auto rounded-md bg-portal-accent hover:bg-portal-accent-hover text-white font-medium px-6 py-2.5 transition-colors disabled:opacity-60"
					>
						{submitting ? t('Signing...') : t('Sign and submit')}
					</button>
					<PaymentLinkSupportLine />
				</div>
			</section>
		</div>
	);
}

/**
 * The payment method question: the account's saved methods (last four only) to choose
 * from, and a new card or bank account added through the same secure fields the
 * storefront uses, saved to the account at once.
 */
function PaymentMethodQuestion({
	token,
	form,
	field,
	methods,
	value,
	error,
	onChange,
	onAdded,
}: {
	token: string;
	form: PublicSentFormModel;
	field: FormFieldDefinitionModel;
	methods: Array<SentFormPaymentMethodOptionModel>;
	value: unknown;
	error: string | null;
	onChange: (value: string | null) => void;
	onAdded: (method: SentFormPaymentMethodOptionModel) => void;
}): JSX.Element {
	const { t } = useTranslation();
	const { isDark } = useTheme();
	const [adding, setAdding] = useState(methods.length === 0);
	const [formKey, setFormKey] = useState(0);
	const chosen = parseFormPaymentMethodAnswer(value);
	const turnstile = useTurnstile({
		siteKey: adding ? form.turnstileSiteKey : null,
		action: form.turnstileAction,
		theme: isDark ? 'dark' : 'light',
	});
	const latestTurnstile = useRef(turnstile);
	latestTurnstile.current = turnstile;

	const api = useMemo<PortalPaymentMethodApiModel>(
		() => ({
			getGatewayConfig: async () => sentFormGatewayConfig(form),
			getCyberSourceCaptureContext: async (type) => getSentFormCaptureContext(token, type),
			submit: async (dto) => {
				const current = latestTurnstile.current;
				const turnstileToken = await current.getToken();
				if (turnstileToken == null) {
					throw new Error(t('Please complete the security check and try again.'));
				}
				try {
					const added = await addSentFormPaymentMethod(token, { paymentMethod: dto, turnstileToken });
					onAdded(added);
					setAdding(false);
					Toast.success(t(`${added.label} was saved to your account.`));
					return true;
				} finally {
					current.reset();
				}
			},
		}),
		[form, token, t, onAdded],
	);

	return (
		<FormField
			name={`sent-form-${field.fieldKey}`}
			label={t(field.label)}
			required={field.isRequired}
			help={field.helpText != null ? t(field.helpText) : undefined}
			error={error != null ? t(error) : undefined}
		>
			<div className="space-y-3" data-testid="payment-method-question">
				{methods.length > 0 && (
					<div role="radiogroup" aria-label={t(field.label)} className="space-y-2">
						{methods.map((method) => (
							<label key={method.id} className="flex items-center gap-3 rounded-md border border-brand-border px-3 py-2 cursor-pointer">
								<input
									type="radio"
									name={`sent-form-${field.fieldKey}`}
									checked={chosen === method.id}
									onChange={() => {
										onChange(formPaymentMethodAnswer(method.id));
									}}
								/>
								<span className="text-sm text-default">{t(method.label)}</span>
								{method.isDefault && <span className="text-xs text-brand-muted">{t('Default')}</span>}
							</label>
						))}
						{!field.isRequired && (
							<button
								type="button"
								className="text-sm underline text-brand-muted"
								onClick={() => {
									onChange(null);
								}}
							>
								{t('No payment method: invoice me')}
							</button>
						)}
					</div>
				)}
				{!adding ? (
					<button
						type="button"
						className="text-sm font-medium text-portal-accent hover:underline"
						onClick={() => {
							setAdding(true);
							setFormKey((key) => key + 1);
						}}
					>
						{t('Add a new card or bank account')}
					</button>
				) : (
					<div className="rounded-md border border-brand-border p-3">
						<PortalAddPaymentMethod
							key={formKey}
							api={api}
							onSuccess={() => undefined}
							onCancel={methods.length > 0 ? () => { setAdding(false); } : undefined}
							supportedPaymentMethods={form.paymentTypes}
							preSelectedPaymentType={
								form.paymentTypes[0]?.paymentMethodType === 'ACH'
									? PaymentMethodTypeEnum.ACH
									: PaymentMethodTypeEnum.CREDIT_CARD
							}
							submitButtonLabel={t('Save to my account')}
							footerContent={
								<div className="space-y-2">
									<div ref={turnstile.containerRef} />
									{turnstile.failed ? (
										<p className="text-sm text-danger">
											{t('The security check could not load. Please refresh the page and try again.')}
										</p>
									) : null}
								</div>
							}
						/>
					</div>
				)}
			</div>
		</FormField>
	);
}
